/**
 * CF-DEMO-TIERS-V1: demo-mode tier economics — a pure, tested mirror of the
 * real backend flip-engine math so practice flips are an HONEST taste of real
 * money (Boost/Mega cost more up front and pay more, at the SAME 50% coin).
 *
 * The 50% coin is provably-fair and tier-independent (handled in CoinFlip); the
 * tier only changes COST + PAYOUT. The arithmetic below is byte-identical to the
 * backend single source of truth (boost-config.ts) and the real-money UI
 * (ui-derivers.ts): gross = stake + stake·feeBps/10000; payout = stake·multBps/10000.
 * It is inlined here (NOT imported from ui-derivers) so this module + its unit test
 * stay free of the i18n/JSX import chain. tier VALUES (multBps/feeBps) still come
 * ONLY from the backend tier_config — never hardcoded (Scorer R4 binding 9); when
 * tierEntry is null (no tier_config / disconnected) → Standard (gross = stake,
 * payout = 2×stake), bit-identical to the prior demo behavior.
 */

import type { TierConfigEntry } from './boost-tier-config';

function computeFee(stakeNano: bigint, feeBps: number): bigint {
  return (stakeNano * BigInt(feeBps)) / 10000n;
}
function computeGross(stakeNano: bigint, feeBps: number): bigint {
  return stakeNano + computeFee(stakeNano, feeBps);
}
function computePayout(stakeNano: bigint, multBps: number): bigint {
  return (stakeNano * BigInt(multBps)) / 10000n;
}

export interface DemoSettleSnapshot {
  multBps: number;
  feeNano: bigint;
  grossNano: bigint;
  payoutNano: bigint;
}

/** Tier-aware demo settle economics from the (already validated) tier_config entry. */
export function deriveDemoSettle(stakeNano: bigint, tierEntry: TierConfigEntry | null): DemoSettleSnapshot {
  if (!tierEntry) {
    return { multBps: 20000, feeNano: 0n, grossNano: stakeNano, payoutNano: stakeNano * 2n };
  }
  return {
    multBps: tierEntry.multiplier_bps,
    feeNano: computeFee(stakeNano, tierEntry.fee_bps),
    grossNano: computeGross(stakeNano, tierEntry.fee_bps),
    payoutNano: computePayout(stakeNano, tierEntry.multiplier_bps),
  };
}

/**
 * Demo balance delta in nano: win → payout − gross; loss → −gross.
 * Standard (gross = stake, payout = 2×stake) reduces to win:+stake / loss:−stake,
 * exactly the prior demo ±stake behavior.
 */
export function demoBalanceDeltaNano(
  snap: { grossNano: bigint; payoutNano: bigint },
  won: boolean,
): bigint {
  return won ? snap.payoutNano - snap.grossNano : -snap.grossNano;
}
